export default {
    tabsAriaLabel: 'Performance profile sections',
    tabs: {
        profil: 'Profil',
        summary: 'Summary',
        assessment: 'Assessment',
        growth: 'Growth',
        objectives: 'Objectives',
        notesAndFeedback: 'Notes',
        competencies: 'Competencies',
    },
    performanceScore: {
        title: 'Performance Score',
    },
    performanceTrend: {
        title: 'Performance Trend',
        chartLabel: 'Level reached at each evaluation cycle',
        nextCycle: 'Next cycle',
        example: {
            badge: 'Example',
            sentence: 'The trend will appear once two cycles are completed.',
            firstCycle: 'First cycle',
            secondCycle: 'Second cycle',
            thirdCycle: 'Third cycle',
        },
        // An empty series reads as "never evaluated". Where cycles exist but all predate the
        // span covered, the card says that instead of denying what happened.
        beforeWindow: {
            badge: 'Older cycles',
            sentence:
                'The trend covers the last five years. Every evaluation cycle of this employee is older than that.',
        },
    },
    competencyProfile: {
        title: 'Competency profile',
        currentPosition: 'Current position',
        targetStep: 'Target position',
        desiredPositionValue: 'Management',
        editDropdownHeader: 'Edit profile for...',
        editCurrentPosition: 'Current position',
        editEvolution: 'Required for evolution',
        editTitle: 'Edit profile of {{name}}',
        selectAnotherProfile: 'Select another profile',
        viewDetails: 'View details',
        selectProfileView: {
            availableProfiles: 'Available profiles',
            createNewProfile: 'Create a new profile',
            createNewProfileDescription: 'Create a custom profile with the guided assistant',
            loading: 'Loading profiles...',
            error: 'Unable to load profiles',
            assigning: 'Assigning profile...',
        },
        competencies_one: '{{count}} competency',
        competencies_other: '{{count}} competencies',
        priorities_one: '{{count}} priority',
        priorities_other: '{{count}} priorities',
        noAssignment: 'No competency profile assigned',
        assignProfile: 'Assign a competency profile',
        assignmentUnavailable: 'Competency profile unavailable',
        additionalCompetencies: 'Additional competencies',
        addOtherCompetencies: 'Add other competencies',
        noAdditionalCompetencies: 'No additional competencies added yet',
        remove: 'Remove',
        removeCompetency: {
            title: 'Remove this competency?',
            message:
                "Once removed, this competency can't be added back directly to this profile. To restore it, you'll need to reassign the profile to the employee. This action can't be undone here.",
            cancel: 'Cancel',
            confirm: 'Remove competency',
        },
        additionalCompetenciesModal: {
            title: 'Add additional competencies',
            subtitle: 'Select competencies from other groups to add to this profile',
            searchPlaceholder: 'Search competencies...',
            noResults: 'No competencies found',
            selectedCount: '{{count}} selected',
            addSelection: 'Add selection',
            skillCount: '{{count}} Competencies',
        },
    },
    skillsetAssessment: {
        title: 'Competency Assessment',
        print: 'Print',
        openHistoryFor: 'View the assessment history for {{skill}}',
        noAssignment: 'No competencies assigned',
        emptyMessage: 'This employee has not been assigned a competency profile yet',
        loadError: 'The competency assessment could not be loaded.',
        customPeriod: 'Custom Period',
        predefinedFilters: 'Predefined filters',
        allCycles: 'All cycles',
        // The period running today, as the server reads it. The picker points it out rather than
        // settling on it: with no filter the server already averages the cycle covering today and
        // completes each competency it does not observe there with its last known rating.
        currentCycle: 'current cycle',
        periodFrom: 'From',
        periodTo: 'To',
        from: 'FROM',
        to: 'TO',
        evaluatorFilter: 'Respondent',
        allEvaluations: 'All evaluations',
        periodFilter: 'Period',
        competencySet: 'Competencies shown',
        currentPositionSkills: 'Essential competencies',
        desiredPositionSkills: 'Competencies required for the target position',
        noDesiredPosition:
            'No target position is recorded for this employee. It is set in the Growth tab.',
        desiredPositionWithoutProfile:
            'No competencies are linked to the target position. Its competency profile is chosen in the Growth tab.',
        proficiencyLevels: {
            title: 'Proficiency levels',
            na: 'n.a.',
            noLevelFound: 'No level found',
            loading: 'Loading proficiency levels...',
        },
        proficiencyModal: {
            subtitle: 'Change the level of proficiency',
            proficiencyLevel: 'Proficiency Level',
            commentLabel: 'Comment (optional)',
            commentPlaceholder: 'Add a comment to explain this review...',
            changeHistory: 'Change history',
            noPreviousChanges: 'No previous changes',
            changeEntry: '{{previous}} → {{next}}',
            noProficiencyLevelFound: 'No proficiency level found',
            proficiencyLevelsLoading: 'Loading proficiency levels…',
            proficiencyLevelsUnavailable:
                'Proficiency levels could not be loaded, so this assessment cannot be saved right now.',
        },
        assessmentHistoryModal: {
            subtitle: 'Assessment History',
            notSpecified: 'Not specified',
            notAnswered: 'Not answered',
            current: 'current',
            empty: 'No assessment history yet',
            sourceSurvey: 'Survey',
            sourceManagerReview: '{{managerLabelCapitalized}} assessment',
            sourceAssessment: 'Assessment',
            answer: 'Answer',
            viewQuestions_one: 'View the {{count}} survey question ({{answered}} answered)',
            viewQuestions_other: 'View the {{count}} survey questions ({{answered}} answered)',
            hideQuestions_one: 'Hide the {{count}} survey question ({{answered}} answered)',
            hideQuestions_other: 'Hide the {{count}} survey questions ({{answered}} answered)',
        },
    },
    assessmentObjectives: {
        title: 'Objectives',
        achievementTitle: 'Objectives achievement',
        // Two figures, two sentences. This tile counts the objectives that are finished, the
        // Objectives tab shows their average progress: the one word "achieved" named both, and
        // the two screens contradicted each other with nothing to say why.
        // The bare key exists so the generated type carries it; i18next resolves the plural.
        completedInPeriod:
            'Over {{period}}, {{name}} finished {{achieved}} of their {{count}} objectives. They stand at {{overall}}% on average.',
        completedInPeriod_one:
            'Over {{period}}, {{name}} finished {{achieved}} of their {{count}} objective. It stands at {{overall}}%.',
        completedInPeriod_other:
            'Over {{period}}, {{name}} finished {{achieved}} of their {{count}} objectives. They stand at {{overall}}% on average.',
        completedAllPeriods:
            'Across all periods, {{name}} finished {{achieved}} of their {{count}} objectives. They stand at {{overall}}% on average.',
        completedAllPeriods_one:
            'Across all periods, {{name}} finished {{achieved}} of their {{count}} objective. It stands at {{overall}}%.',
        completedAllPeriods_other:
            'Across all periods, {{name}} finished {{achieved}} of their {{count}} objectives. They stand at {{overall}}% on average.',
        noObjectivesInPeriod: 'No objective is tracked for {{name}} in {{period}}.',
        noObjectives: 'No objective is tracked for {{name}}.',
        rateLabel: 'Overall progression',
        loadError: 'The objectives could not be loaded.',
        viewAll: 'View all objectives',
    },
    evaluations: {
        loading: 'Loading…',
        pagerGoToPage: 'Go to page {{page}}',
        disabledTooltip:
            "You don't have sufficient permission to perform this action. Please contact your administrator.",
        self: {
            title: 'Self-Assessment',
            description:
                '{{name}} completed self-assessments, which are not included in the employee performance score. Their purpose is solely to help each {{managerLabel}} prepare the evaluation.',
            startButton: 'Start a Self Assessment',
            openLabel: 'Open the self-assessment of {{date}}',
            noAssessments: 'No self-assessment',
            loadError: 'Self-assessments could not be loaded.',
        },
        evaluation360: {
            title: '360 Feedback',
            description:
                'Competencies evaluated by coworkers, but these assessments do not affect the employee performance score. They are intended solely to assist each {{managerLabel}} in preparing the evaluation.',
            startButton: 'Start a 360 Review',
            completion: 'Completion',
            invitedRespondents_one: '{{count}} respondent invited',
            invitedRespondents_other: '{{count}} respondents invited',
            openLabel: 'Open the 360 feedback of {{date}}, {{name}}',
            noEvaluations: 'No 360 feedback',
            loadError: '360 feedback could not be loaded.',
        },
        manager: {
            title: 'Assessments by {{managerLabel}}',
            description:
                'Performance evaluations conducted by the {{managerLabel}} to assess employee progress and provide feedback.',
            startButton: 'Start a {{managerLabelCapitalized}} Assessment',
            selfAssessmentWarningTitle: 'Cannot Create Assessment',
            selfAssessmentWarningMessage:
                'You cannot create an assessment by {{managerLabel}} for yourself.',
            openLabel: 'Open the {{managerLabel}} assessment of {{date}} by {{name}}',
            noAssessments: 'No assessment by {{managerLabel}}',
            loadError: 'Assessments by {{managerLabel}} could not be loaded.',
        },
    },
    assessment: {
        forbidden: {
            title: 'Your results live in your portal',
            description:
                'This tab shows the evaluation as your {{managerLabel}} works on it: nobody reads their own. What you fill in yourself stays within reach.',
            viewPortal: 'View my portal',
        },
    },
    growth: {
        forbidden: {
            ownProfile: {
                title: 'Your growth is prepared together',
                description:
                    'You do not have access to this tab on your own profile. The career path, the aspirations and the next target are built with your {{managerLabel}}, during your career meetings.',
                viewPortal: 'View my portal',
            },
            outOfScope: {
                title: 'This growth is out of your reach',
                description:
                    'The career path, the aspirations and the next target only read on the employees you manage. Someone who has left the company is no longer among them, even where their profile stays open to you.',
            },
        },
        empty: {
            title: "{{name}}'s growth starts with a conversation",
            titleWithoutName: 'Growth starts with a conversation',
            description:
                'Record their aspirations, or plan a career meeting. The career path, the competencies to develop and the target are built from there.',
            recordAspirations: 'Record aspirations',
            planMeeting: 'Plan a meeting',
        },
        careerObjective: {
            title: 'Career objective',
            targetStep: 'Target position',
            importanceFor: 'Importance for {{name}}: {{level}}/{{max}}',
            developmentPath: 'Development path',
            empty: 'No career aspiration recorded',
            recordedWithoutTarget: 'Aspirations recorded, no target position',
            emptyHint:
                'A "Career aspirations" meeting captures the target position and how much it matters.',
            reEvaluate: 'Re-evaluate career aspirations',
        },
        timeline: {
            title: 'Career path',
            editSteps: 'Edit steps',
            empty: 'No career step yet.',
            addFirstStep: 'Add a first career step',
            scrollLeft: 'Scroll left',
            scrollRight: 'Scroll right',
            currentBadge: 'Current',
            targetBadge: 'Target position',
            present: 'present',
            undated: 'No date',
            targetDate: 'Aimed for {{year}}',
            clusterCount_one: '{{count}} step',
            clusterCount_other: '{{count}} steps',
            chartAria: 'Career path timeline, {{count}} steps',
        },
        stepsEditor: {
            title: 'Edit career steps',
            subtitle: 'Add, edit or remove the steps of the career path.',
            empty: 'No step yet. Click "Add step" to get started.',
            addStep: 'Add step',
            newStep: 'New step',
            editStep: 'Edit step',
            positionTitle: 'Position title',
            positionPlaceholder: 'E.g. Senior analyst',
            startDate: 'Start date',
            horizonOptional: 'Target horizon (optional)',
            noHorizon: 'Horizon not set',
            clearHorizon: 'No horizon',
            endDate: 'End date',
            type: 'Step type',
            kinds: {
                Past: 'Past position',
                Current: 'Current position',
                Target: 'Target position',
            },
            competencyProfile: 'Competency profile',
            profileOptional: 'Competency profile (optional)',
            profileLinkedToCurrent: "Linked to the employee's current profile",
            noAssignedProfile: 'No profile currently assigned',
            noProfile: 'No profile',
            profilePlaceholder: 'Choose a profile…',
            searchProfile: 'Search for a profile…',
            noProfileFound: 'No profile found.',
            profilePrefix: 'Profile:',
            ongoing: 'ongoing',
            titleRequired: 'The position title is required',
            startDateRequired: 'The start date is required',
            titleTooLong: 'The title cannot be longer than {{max}} characters',
            endBeforeStart: 'The end date must come after the start date',
            endDateRequired: 'A past position needs its end date',
            pastEndInFuture: 'A past position cannot end after today',
            added: 'Step added',
            updated: 'Step updated',
            deleted: 'Step deleted',
            saveError: 'The step could not be saved',
            deleteError: 'The step could not be deleted',
            deleteTitle: 'Delete this step?',
            clearAdjustmentsTitle: 'Clear the adjustments?',
            clearAdjustmentsMessage:
                'The competencies added or removed for this employee on the target profile will be cleared.',
            deleteMessage:
                '"{{title}}" will disappear from the career path. This cannot be undone.',
            editAria: 'Edit step {{title}}',
            deleteAria: 'Delete step {{title}}',
        },
        nextTarget: {
            title: 'Next target',
            newTarget: 'New target',
            currentPosition: 'Current position:',
            desiredPosition: 'Target position:',
            competencyProfile: 'Competency profile:',
            adjustedProfile: 'Based on {{name}}, adjusted',
            undefined: 'Not set',
            editCurrentAria: 'Current position actions',
            editCurrentStep: 'Edit the current position',
            editCurrentProfile: 'Edit the competency profile',
            editDesiredAria: 'Target position actions',
            editDesiredStep: 'Edit the target position',
            emptyProfile: {
                description:
                    'No profile is associated with this employee yet. Select their current profile and their target profile to enable progression tracking.',
                selectCurrent: 'Select the current profile',
                selectDesired: 'Select the target profile',
                undefined: 'Not set',
            },
        },
        targetProfile: {
            titleOptional: 'Target position name (optional)',
            titleHelp:
                '{{name}} may not be aiming for a different position. When named, it stays the same when you select another profile, and shows in their Growth tab.',
            titlePlaceholder: 'E.g. Program director',
            internalNote:
                'Internal notes: {{name}} sees neither the position name nor the horizon, only their {{managerPluralLabel}} and HR do.',
            saved: 'Target position saved',
        },
        evolution: {
            journey: 'Evolution journey',
            subtitle: 'Developing mastery of their ambitions',
            targetNote:
                'Here is what {{firstName}} will need to develop to reach the target position.',
            start: 'Start',
            consolidation: 'Consolidation',
            expertise: 'Expertise',
            ready: 'Ready for evolution',
        },
        skills: {
            title: 'Competencies for the next level',
            empty: 'No competency assessed for the target profile. Add competencies to the profile or complete an assessment.',
            notAssessed: 'Not assessed',
            allReached: 'Every competency of the target profile meets the required level.',
        },
        score: {
            title: 'Performance score',
            currentProfile: 'Current profile:',
            editCurrentProfile: 'Edit the current profile',
            avatarAlt: 'Photo of {{name}}',
            outOfLevels: '{{value}} / {{total}}',
            notEvaluated: 'Not evaluated yet',
            // The same emptiness read through a period filter does not say the same thing: the
            // tile names the period rather than stating, of the person, what is only true of the
            // cycle being looked at -- the trend beside it shows every cycle.
            notEvaluatedInPeriod: 'No assessment · {{period}}',
            levelsError: 'Level scale unavailable',
            noLevels: 'No proficiency level configured',
            // The calculated level and the one a manager applies by hand can disagree, and the
            // filter that bounds the score shows on one tab only: the tile names both. Both read
            // the same evaluations, the most recent ones -- a manually applied level is the latest
            // evaluation's, not a choice looked up over the whole history.
            scope: {
                calculated: 'Calculated level - {{period}}',
                managerChosen: 'Manually applied level - {{period}}',
                mostRecentAssessments: 'Most recent assessments',
            },
            // When a manager has kept a level themselves, the scale may reach another one from the
            // same score -- and that is the one the arc draws. The tile names it: two answers on
            // one drawing are read, not guessed at.
            calculatedGap: 'The calculation gives “{{level}}”',
            // The tile names the level and keeps the arithmetic to itself: these sentences say
            // which assessments are counted, never how they are combined. The third is the one
            // thing a reader cannot work out from the screen, and the fourth answers the question
            // the tile raises when it says the level was applied by hand: who decided, and instead
            // of what.
            howItIsRead: {
                label: 'How this score is worked out',
                managerChose:
                    'The {{managerLabel}} chose to change this employee’s score level themselves, instead of letting the competency score resolve it.',
                submittedOnly:
                    'Only {{managerLabel}} assessments that have been submitted count, as soon as they are submitted.',
                oneVoicePerEvaluator: 'Each evaluator counts once, with their most recent rating.',
                keepsLastKnown:
                    'With no filter, a competency nobody assessed in this cycle keeps its last known value; a filtered score counts only the evaluations the filter lets through.',
            },
        },
        aspiration: {
            title: 'Aspiration assessment results',
            subtitle: 'Summary of the latest "Career aspirations" meeting',
            emptyHint: 'Aspirations update automatically after a "Career aspirations" meeting.',
            planMeeting: 'Plan a career aspiration meeting',
            edit: 'Edit details',
            openMeeting: 'View meeting',
            history: 'Choose a meeting',
            historyAria: '"Career aspirations" meeting history',
            pending: 'Pending',
            pendingHint:
                'Nothing has been recorded for this meeting yet. Open the meeting to fill in the aspirations.',
            loadError: 'This aspiration could not be loaded.',
            retry: 'Try again',
            notAnswered: 'Not answered',
            developmentPath: 'Which development path best fits {{name}} right now?',
            positionTitle: 'Target position name',
            competencyProfile: 'Target competency profile',
            importance: 'How important is it for {{name}} to get a new position?',
            importanceOutOf: '{{level}} / {{max}}',
            importanceLevels: {
                '1': 'Not important',
                '2': 'Slightly important',
                '3': 'Moderately important',
                '4': 'Important',
                '5': 'Very important',
                unknown: 'Not assessed',
            },
            responsibilities: 'Does eventually taking on new responsibilities meet their needs?',
            alignment:
                'Is there a shared time alignment with {{name}} on their capacity to grow? What is the shared vision for a possible evolution?',
            factors: 'What factors most influence their desire to obtain a new role?',
            supportNeeds: 'What support would {{name}} need to move forward?',
            blockers: 'Are there factors holding back or complicating their growth right now?',
            departureRisk:
                'In your view, what is the risk that {{name}} leaves the organization if their aspirations are not met?',
            confidential: 'Confidential',
            confidentialNote: 'Visible to you and your management line.',
            record: 'Record aspirations',
            form: {
                title: 'Career aspirations',
                subtitle: 'Support {{name}} into their next professional chapter',
                visibility: 'Visible to you and your management line, never to the employee.',
                positionPlaceholder: 'E.g. Manager, Senior analyst…',
                profilePlaceholder: 'Choose a profile…',
                searchProfile: 'Search for a profile…',
                noProfile: 'No profile',
                profileHint:
                    "The target role's competency profile is optional. Even if the employee isn't looking for a new role, it can be worth assigning one when there's a mid- to long-term vision. It can be updated at any time.",
                importanceScale: 'From 1 (not important) to 5 (very important)',
                alignmentPlaceholder:
                    'Note the time horizon, the milestones and the shared vision…',
                supportNeedsPlaceholder: 'Select one or more supports…',
                searchSupport: 'Search…',
                supportNeedsOtherChoice: 'Other',
                supportNeedsOther: 'Other support',
                supportNeedsOtherRequired: 'Describe the other support, or deselect "Other"',
                supportNeedsOtherPlaceholder: 'Describe the other support',
                blockersNotesPlaceholder: 'Details (optional)',
                footerHint: 'Your answers are saved to the career record.',
                save: 'Save aspirations',
                saved: 'Career aspirations saved',
                saveError: 'The aspirations could not be saved',
                tooLong: 'This answer is too long',
                blankSaveTitle: 'No answer entered',
                blankSaveMessage: 'No question has been answered. Save the aspirations anyway?',
                blankSaveConfirm: 'Save anyway',
                blankSaveCancel: 'Keep answering',
            },
            paths: {
                EvolveNewRole: 'Move into a new role',
                DeepenExpertise: 'Deepen expertise in the current role',
                ThriveCurrentRole: 'Thrive in the current role',
                ExploreOtherRoles: 'Explore other types of roles',
                StillExploring: 'Aspiration still to be clarified',
            },
            responsibilityOptions: {
                Yes: 'Yes',
                No: 'No',
                Partial: 'Partially',
            },
            factorOptions: {
                ProfessionalGrowth: 'Professional growth',
                TechnicalChallenges: 'Technical challenges',
                HigherCompensation: 'Higher compensation',
                Autonomy: 'Autonomy',
                Leadership: 'Leadership',
                WorkLifeBalance: 'Work-life balance',
                Recognition: 'Recognition',
                Impact: 'Impact',
            },
            supportNeedOptions: {
                FormalTraining: 'Formal training (course, certification, degree)',
                InternalMentoring: 'Internal mentoring',
                ProfessionalCoaching: 'One-on-one professional coaching',
                MoreManagerMeetings: 'More frequent meetings with their {{managerLabel}}',
                MoreFrequentFeedback: 'More regular feedback on their work',
                StretchProject: 'A large project or stretch assignment',
                CrossFunctionalCommittee:
                    'Participation in a cross-functional committee or project',
                ProgressiveDelegation: 'Progressive delegation of new responsibilities',
                Shadowing: 'Job shadowing and on-the-job observation',
                LeadershipVisibility: 'Visibility with leadership',
                TemporaryAssignment: 'Temporary assignment or job rotation',
                IndustryEvents: 'Participation in industry events (conferences, networking)',
                PeerLearningGroup: 'Peer learning group',
                SelfPacedResources: 'Self-paced learning resources (reading, online training)',
                DevelopmentBudget: 'Development budget',
                ProtectedTime: 'Protected time in their schedule for development',
                CareerPathClarity: 'A clear view of their career path steps',
                TrainOrMentorOthers: 'Opportunities to train or mentor others',
                NoSupportNeeded: 'No particular support for now',
            },
            // Group headings of the support needs, unread while the served library's multi-select
            // has no groups of options; they are used as they are once it does.
            supportNeedGroups: {
                learning: 'Learning and training',
                coaching: 'Coaching and mentoring',
                experience: 'Learning by doing',
                exposure: 'Exposure and network',
                conditions: 'Enabling conditions',
                other: 'Uncategorized',
                custom: 'Custom',
            },
            blockerOptions: {
                WorkloadAvailability: 'Availability or current workload',
                SkillsToConsolidate: 'Competencies to consolidate first',
                Confidence: 'Lack of confidence',
                PersonalConstraints: 'Personal constraints',
                FewOrgOpportunities: 'Few opportunities in the organization for now',
                NoneParticular: 'No particular blocker',
            },
            departureRiskOptions: {
                Low: 'Low',
                Moderate: 'Moderate',
                High: 'High',
            },
        },
        notes: {
            title: 'Career journey notes',
            placeholder: 'Share an observation about their career journey…',
            deleteTitle: 'Delete this note?',
            deleteMessage:
                'The note will disappear from the career journey. This cannot be undone.',
            empty: 'No note yet. The first observation goes here.',
            loadError: 'The career journey notes could not be loaded.',
            forbidden: 'You may not read this employee’s notes.',
            createError: 'The note could not be saved',
            updateError: 'The note could not be updated',
            deleteError: 'The note could not be deleted',
            pageSummary: '{{start}} to {{end}} of {{total}}',
        },
    },
    objectives: {
        period: {
            label: 'Period:',
        },
        overallProgression: 'Overall progression',
        inProgressLegend: 'In progress',
        metrics: {
            total: 'Total objectives',
            achieved: 'Objectives achieved',
            overdue: 'Overdue objectives',
        },
        listTitle: 'Objectives',
        pageSummary: '{{start}} to {{end}} of {{total}}',
        empty: 'No objective for this employee in this period.',
        openObjective: 'Open objective {{title}}',
        progressAria: 'Progress: {{value}} %',
    },
    employeeNotes: {
        unknownAuthor: 'Unknown author',
        you: 'You',
        actionsAria: 'Note actions',
        edit: 'Edit',
        delete: 'Delete',
        save: 'Save',
        editorAria: 'Note content',
        visibility: 'Visible to you and your management line, never to the employee.',
        fromMeeting: 'Seen in a meeting',
        fromDeletedMeeting: 'Seen in a meeting that has since been deleted',
        fromAssessment: 'Seen in an assessment',
        created: 'Note added',
        updated: 'Note updated',
        deleted: 'Note deleted',
        categoryAria: 'Note folder',
        categories: {
            General: 'General',
            Performance: 'Performance',
            Personal: 'Personal',
            Collaboration: 'Meetings',
            Career: 'Career',
        },
    },
    notesTab: {
        managerTitle: "{{managerLabelCapitalized}}'s notes",
        managerDescription:
            "You can add notes to {{name}}'s file here at any time. Visible to you and your management line, never to the employee.",
        title: 'Employee notes',
        folderAll: 'All',
        foldersAria: 'Note folders',
        empty: 'No notes in this folder.',
        loadError: 'The notes could not be loaded.',
        forbidden: 'You may not read this employee’s notes.',
        add: 'Add a note',
        placeholder: 'Write your note…',
        deleteTitle: 'Delete this note?',
        deleteMessage: 'The note will disappear from the file. This cannot be undone.',
        createError: 'The note could not be saved',
        updateError: 'The note could not be updated',
        deleteError: 'The note could not be deleted',
        pageSummary: '{{start}} to {{end}} of {{total}}',
    },
    loading: 'Loading...',
    error: 'Error loading performance data',
    noData: 'No performance data available for this employee',
    notFound: 'This employee cannot be found, or their profile is not within your reach.',
    comingSoon: 'Coming soon',
    back: 'Back',
};
